'use client'

import { useState, useEffect } from 'react'
import * as z from 'zod'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { toast } from 'sonner'
import { nanoid } from 'nanoid'
import { Monitor, Smartphone, Plus, X, Wand2, Lock, Unlock } from 'lucide-react'
import { useEditor, EditorContent } from '@tiptap/react'
import TiptapStarterKit from '@tiptap/starter-kit'
import Link from '@tiptap/extension-link'
import TextStyle from '@tiptap/extension-text-style'
import Color from '@tiptap/extension-color'
import { Bold, Italic, List, ListOrdered, Link as LinkIcon } from 'lucide-react'
import {
  MultiSelector,
  MultiSelectorContent,
  MultiSelectorInput,
  MultiSelectorItem,
  MultiSelectorList,
  MultiSelectorTrigger,
} from '@/components/extension/multi-select'

import type { Template } from '@/lib/types'
import { uploadBlobToCdn, formatUrl } from '@/lib/utils'
import {
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { popModal } from '@/components/modals'
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { CATEGORIES } from '@/lib/constant'

// Tiptap Rich Text Editor Component (copied from edit-starter-kit.tsx)
const RichTextEditor = ({
  value,
  onChange,
}: {
  value: string
  onChange: (value: string) => void
}) => {
  const editor = useEditor({
    extensions: [
      TiptapStarterKit,
      Link.configure({
        openOnClick: false,
        HTMLAttributes: {
          class: 'text-blue-500 underline',
        },
      }),
      TextStyle,
      Color,
    ],
    content: value,
    onUpdate: ({ editor }) => {
      onChange(editor.getHTML())
    },
  })

  if (!editor) {
    return null
  }

  const addLink = () => {
    const url = window.prompt('Enter URL:')
    if (url) {
      editor.chain().focus().setLink({ href: url }).run()
    }
  }

  return (
    <div className="border rounded-md bg-gray-900">
      {/* Toolbar */}
      <div className="border-b border-gray-700 p-2 flex gap-1 flex-wrap bg-gray-800">
        <button
          type="button"
          onClick={() => editor.chain().focus().toggleBold().run()}
          className={`p-2 rounded hover:bg-gray-600 text-white ${editor.isActive('bold') ? 'bg-gray-600' : ''}`}
          title="Bold"
        >
          <Bold className="w-4 h-4" />
        </button>
        <button
          type="button"
          onClick={() => editor.chain().focus().toggleItalic().run()}
          className={`p-2 rounded hover:bg-gray-600 text-white ${editor.isActive('italic') ? 'bg-gray-600' : ''}`}
          title="Italic"
        >
          <Italic className="w-4 h-4" />
        </button>
        <button
          type="button"
          onClick={() => editor.chain().focus().toggleBulletList().run()}
          className={`p-2 rounded hover:bg-gray-600 text-white ${editor.isActive('bulletList') ? 'bg-gray-600' : ''}`}
          title="Bullet List"
        >
          <List className="w-4 h-4" />
        </button>
        <button
          type="button"
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
          className={`p-2 rounded hover:bg-gray-600 text-white ${editor.isActive('orderedList') ? 'bg-gray-600' : ''}`}
          title="Numbered List"
        >
          <ListOrdered className="w-4 h-4" />
        </button>
        <button
          type="button"
          onClick={addLink}
          className={`p-2 rounded hover:bg-gray-600 text-white ${editor.isActive('link') ? 'bg-gray-600' : ''}`}
          title="Add Link"
        >
          <LinkIcon className="w-4 h-4" />
        </button>
      </div>

      {/* Editor Content */}
      <EditorContent
        editor={editor}
        className="prose prose-invert max-w-none p-4 min-h-[300px] focus:outline-none text-white [&_.ProseMirror]:text-white [&_.ProseMirror]:min-h-[250px] [&_.ProseMirror]:outline-none"
      />
    </div>
  )
}

interface Props {
  templateData: Template
  onSuccess?: () => void
  onClose?: () => void
}

const formSchema = z.object({
  title: z.string().min(2, {
    message: 'Title must be at least 2 characters.',
  }),
  slug: z.string().optional(),
  category: z.array(z.string()).min(1, {
    message: 'Please select at least one category.',
  }),
  sub_categories: z.array(z.string()).optional(),
  device_view: z.enum(['desktop', 'mobile']),
  ai_prompt: z.string().optional(),
  image: z
    .any()
    .optional()
    .refine(
      (files) => !files || files.length === 0 || files.length === 1,
      'Please select only one image.',
    )
    .refine(
      (files) =>
        !files || files.length === 0 || files[0]?.type?.startsWith('image/'),
      'Please select an image file.',
    ),
  ranking: z.number().optional(),
  base_template_id: z.string().optional(),
  ai_template_name: z.string().optional(),
  ai_overview: z
    .object({
      title: z.string().optional(),
      content: z.string().optional(),
      features: z.array(z.string()).optional(),
      usecases: z.array(z.string()).optional(),
    })
    .optional(),
  ai_tips: z
    .object({
      summary: z.string().optional(),
      points: z.array(z.string()).optional(),
      target_users: z.array(z.string()).optional(),
    })
    .optional(),
  ai_faq: z
    .object({
      items: z
        .array(
          z.object({
            question: z.string(),
            answer: z.string(),
          }),
        )
        .optional(),
    })
    .optional(),
  ai_description: z.string().optional(),
  share_twitter_tag: z.string().optional(),
  share_social_tag: z.string().optional(),
  share_linkedin_tag: z.string().optional(),
  seo_title: z
    .string()
    .max(60, {
      message: 'SEO Page Title must be at most 60 characters.',
    })
    .optional(),
  seo_description: z
    .string()
    .max(155, {
      message: 'Social Description must be at most 155 characters.',
    })
    .optional(),
  twitter_title: z
    .string()
    .max(70, {
      message: 'Twitter Title must be at most 70 characters.',
    })
    .optional(),
  twitter_description: z
    .string()
    .max(200, {
      message: 'Twitter Description must be at most 200 characters.',
    })
    .optional(),
  optional_prompt: z.string().optional(),
  template_remix_title: z.string().optional(),
  template_remix_subtitle: z.string().optional(),
  template_remix_prompt_placeholder: z.string().optional(),
  questions_rich_text: z.string().optional(),
})

type FormData = z.infer<typeof formSchema>

export default function EditTemplateModal({
  templateData,
  onSuccess,
  onClose = popModal,
}: Props) {
  const [loading, setLoading] = useState(false)
  const [existingImageUrl, setExistingImageUrl] = useState<string | null>(
    templateData?.image_url || null,
  )
  const [isSlugLocked, setIsSlugLocked] = useState(!!templateData?.slug)

  // State for managing dynamic arrays
  const [features, setFeatures] = useState<string[]>(
    templateData?.ai_overview?.features || [''],
  )
  const [usecases, setUsecases] = useState<string[]>(
    templateData?.ai_overview?.usecases || [''],
  )
  const [tipsPoints, setTipsPoints] = useState<string[]>(
    templateData?.ai_tips?.points || [''],
  )
  const [tipsTargetUsers, setTipsTargetUsers] = useState<string[]>(
    templateData?.ai_tips?.target_users || [''],
  )
  const [faqItems, setFaqItems] = useState<
    { question: string; answer: string }[]
  >(templateData?.ai_faq?.items || [{ question: '', answer: '' }])

  // Helper functions for managing arrays
  const addFeature = () => setFeatures([...features, ''])
  const removeFeature = (index: number) => {
    if (features.length > 1) setFeatures(features.filter((_, i) => i !== index))
  }
  const updateFeature = (index: number, value: string) => {
    const updatedFeatures = [...features]
    updatedFeatures[index] = value
    setFeatures(updatedFeatures)
  }

  const addUsecase = () => setUsecases([...usecases, ''])
  const removeUsecase = (index: number) => {
    if (usecases.length > 1) setUsecases(usecases.filter((_, i) => i !== index))
  }
  const updateUsecase = (index: number, value: string) => {
    const updatedUsecases = [...usecases]
    updatedUsecases[index] = value
    setUsecases(updatedUsecases)
  }

  const addTipsPoint = () => setTipsPoints([...tipsPoints, ''])
  const removeTipsPoint = (index: number) => {
    if (tipsPoints.length > 1)
      setTipsPoints(tipsPoints.filter((_, i) => i !== index))
  }
  const updateTipsPoint = (index: number, value: string) => {
    const updatedPoints = [...tipsPoints]
    updatedPoints[index] = value
    setTipsPoints(updatedPoints)
  }

  const addTipsTargetUser = () => setTipsTargetUsers([...tipsTargetUsers, ''])
  const removeTipsTargetUser = (index: number) => {
    if (tipsTargetUsers.length > 1)
      setTipsTargetUsers(tipsTargetUsers.filter((_, i) => i !== index))
  }
  const updateTipsTargetUser = (index: number, value: string) => {
    const updatedTargetUsers = [...tipsTargetUsers]
    updatedTargetUsers[index] = value
    setTipsTargetUsers(updatedTargetUsers)
  }

  const addFaqItem = () =>
    setFaqItems([...faqItems, { question: '', answer: '' }])
  const removeFaqItem = (index: number) => {
    if (faqItems.length > 1) setFaqItems(faqItems.filter((_, i) => i !== index))
  }
  const updateFaqItem = (
    index: number,
    field: 'question' | 'answer',
    value: string,
  ) => {
    const updatedItems = [...faqItems]
    updatedItems[index][field] = value
    setFaqItems(updatedItems)
  }

  const form = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      title: templateData?.title || '',
      slug: templateData?.slug || '',
      category: Array.isArray(templateData?.category)
        ? templateData.category
        : templateData?.category
          ? [templateData.category]
          : [],
      sub_categories: Array.isArray(templateData?.sub_categories)
        ? templateData.sub_categories
        : templateData?.sub_categories
          ? [templateData.sub_categories]
          : [],
      device_view: templateData?.device_view || 'desktop',
      ai_prompt: templateData?.ai_prompt || '',
      ranking: templateData?.ranking || 0,
      base_template_id: templateData?.base_template_id || '',
      ai_template_name: templateData?.ai_template_name || '',
      ai_overview: {
        title: templateData?.ai_overview?.title || '',
        content: templateData?.ai_overview?.content || '',
        features: templateData?.ai_overview?.features || [],
        usecases: templateData?.ai_overview?.usecases || [],
      },
      ai_tips: templateData?.ai_tips || {
        summary: '',
        points: [],
        target_users: [],
      },
      ai_faq: templateData?.ai_faq || { items: [] },
      ai_description: templateData?.ai_description || '',
      share_twitter_tag: templateData?.share_twitter_tag || '',
      share_social_tag: templateData?.share_social_tag || '',
      share_linkedin_tag: templateData?.share_linkedin_tag || '',
      seo_title: templateData?.seo_title || '',
      seo_description: templateData?.seo_description || '',
      twitter_title: templateData?.twitter_title || '',
      twitter_description: templateData?.twitter_description || '',
      optional_prompt: templateData?.optional_prompt || '',
      template_remix_title:
        templateData?.template_remix_title || 'Start Your Project',
      template_remix_subtitle:
        templateData?.template_remix_subtitle || 'Tell us about your project',
      template_remix_prompt_placeholder:
        templateData?.template_remix_prompt_placeholder || 'Tell us...',
      questions_rich_text: templateData?.questions_rich_text || '',
    },
  })

  const imageFile = form.watch('image')?.[0]
  const hasImage = Boolean(existingImageUrl) || Boolean(imageFile)

  const handleGenerateSlug = () => {
    const title = form.getValues('title')
    if (title) {
      form.setValue('slug', formatUrl(title), { shouldValidate: true })
    } else {
      toast.warning('Please enter a title first to generate a slug.')
    }
  }

  async function onSubmit(values: FormData) {
    try {
      setLoading(true)
      let imageUrl = existingImageUrl
      if (values.image?.[0]) {
        imageUrl = await uploadBlobToCdn(
          values.image[0],
          `templates/${nanoid()}.${values.image[0].name.split('.').pop()}`,
          values.image[0].type,
        )
      }
      if (!imageUrl) {
        toast.error('An image is required')
        setLoading(false)
        return
      }

      const endpoint = `/api/dashboard/templates/${templateData?._id}`
      const aiOverview = values.ai_overview
        ? {
            title: values.ai_overview.title || '',
            content: values.ai_overview.content || '',
            features: features.filter((f) => f.trim() !== ''),
            usecases: usecases.filter((u) => u.trim() !== ''),
          }
        : undefined
      const aiTips = values.ai_tips
        ? {
            summary: values.ai_tips.summary || '',
            points: tipsPoints.filter((p) => p.trim() !== ''),
            target_users: tipsTargetUsers.filter((u) => u.trim() !== ''),
          }
        : undefined
      const aiFaq = values.ai_faq
        ? {
            items: faqItems.filter(
              (item) =>
                item.question.trim() !== '' && item.answer.trim() !== '',
            ),
          }
        : undefined

      const res = await fetch(endpoint, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...values,
          slug: values.slug, // Ensure the slug is sent
          image_url: imageUrl,
          ai_overview: aiOverview,
          ai_tips: aiTips,
          ai_faq: aiFaq,
        }),
      })

      if (!res.ok) throw new Error('Failed to update template')

      toast.success('Template updated successfully')
      onSuccess?.()
      onClose()
    } catch (error) {
      console.error(error)
      toast.error('Failed to update template')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (templateData?.image_url) {
      setExistingImageUrl(templateData.image_url)
    }
  }, [templateData])

  const clearImage = () => {
    form.setValue('image', null)
    setExistingImageUrl(null)
  }

  return (
    <DialogContent className="max-w-4xl max-h-[90vh] flex flex-col">
      <DialogHeader>
        <DialogTitle>Edit Template</DialogTitle>
        <DialogDescription>
          Edit template details for {templateData?.title}
        </DialogDescription>
      </DialogHeader>
      <div className="flex-1 overflow-y-auto pr-2">
        <Form {...form}>
          <form
            id="edit-template-form"
            onSubmit={form.handleSubmit(onSubmit)}
            className="space-y-4"
          >
            <Tabs defaultValue="basic" className="w-full">
              <TabsList className="grid w-full grid-cols-3 mb-6">
                <TabsTrigger value="basic">Basic Info</TabsTrigger>
                <TabsTrigger value="questions">Questions</TabsTrigger>
                <TabsTrigger value="advanced">Advanced</TabsTrigger>
              </TabsList>

              <TabsContent value="basic" className="space-y-6 mt-6">
                <FormField
                  control={form.control}
                  name="title"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Title</FormLabel>
                      <FormControl>
                        <Input placeholder="Enter template title" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="slug"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>URL Slug</FormLabel>
                      <div className="flex items-center gap-2">
                        <FormControl>
                          <Input
                            placeholder="e.g. my-awesome-template"
                            {...field}
                            disabled={isSlugLocked}
                          />
                        </FormControl>
                        {templateData?.slug ? (
                          <Button
                            type="button"
                            variant="outline"
                            size="icon"
                            onClick={() => setIsSlugLocked(!isSlugLocked)}
                          >
                            {isSlugLocked ? (
                              <Lock className="h-4 w-4" />
                            ) : (
                              <Unlock className="h-4 w-4" />
                            )}
                          </Button>
                        ) : (
                          <Button
                            type="button"
                            variant="outline"
                            size="icon"
                            onClick={handleGenerateSlug}
                          >
                            <Wand2 className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                      <FormMessage />
                      {templateData?.slug && (
                        <p className="text-sm text-amber-600 dark:text-amber-400">
                          Warning: Changing the URL slug will cause previously
                          indexed pages to return 404 errors. This field is
                          locked to prevent SEO issues.
                        </p>
                      )}
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="base_template_id"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Base Template ID</FormLabel>
                      <FormControl>
                        <Input
                          placeholder="Enter base template ID (optional)"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                      <p className="text-sm text-gray-500">
                        ID of the base template this template extends from
                      </p>
                    </FormItem>
                  )}
                />

                {/* ... (rest of the form fields remain the same) ... */}

                <div className="pt-2 pb-2">
                  <FormField
                    control={form.control}
                    name="seo_title"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>SEO Page Title</FormLabel>
                        <FormControl>
                          <Input
                            maxLength={60}
                            placeholder="请输入 SEO 页面标题 (≤60字)"
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="seo_description"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Social Description</FormLabel>
                        <FormControl>
                          <Textarea
                            maxLength={155}
                            placeholder="请输入 Social Description (≤155字)"
                            className="min-h-[60px]"
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="twitter_title"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Twitter Title</FormLabel>
                        <FormControl>
                          <Input
                            maxLength={70}
                            placeholder="请输入 Twitter 标题 (≤70字)"
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="twitter_description"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Twitter Description</FormLabel>
                        <FormControl>
                          <Textarea
                            maxLength={200}
                            placeholder="请输入 Twitter 描述 (≤200字)"
                            className="min-h-[60px]"
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="optional_prompt"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Optional Prompt</FormLabel>
                        <FormControl>
                          <Textarea
                            placeholder="Enter optional prompt (optional)"
                            className="min-h-[80px]"
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
                <FormField
                  control={form.control}
                  name="category"
                  render={({ field }) => (
                    <FormItem className="w-full">
                      <FormLabel>Category</FormLabel>
                      <MultiSelector
                        onValuesChange={field.onChange}
                        values={field.value}
                      >
                        <MultiSelectorTrigger>
                          <MultiSelectorInput placeholder="Choose template category" />
                        </MultiSelectorTrigger>
                        <MultiSelectorContent>
                          <MultiSelectorList>
                            {CATEGORIES.map((category) => (
                              <MultiSelectorItem
                                key={category.id}
                                value={category.id}
                              >
                                <span>{category.label}</span>
                              </MultiSelectorItem>
                            ))}
                          </MultiSelectorList>
                        </MultiSelectorContent>
                      </MultiSelector>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="sub_categories"
                  render={({ field }) => (
                    <FormItem className="w-full">
                      <FormLabel>Sub-Categories</FormLabel>
                      <MultiSelector
                        onValuesChange={field.onChange}
                        values={field.value || []}
                      >
                        <MultiSelectorTrigger>
                          <MultiSelectorInput placeholder="Choose template sub-categories (optional)" />
                        </MultiSelectorTrigger>
                        <MultiSelectorContent>
                          <MultiSelectorList>
                            <MultiSelectorItem value="Pet Store and Marketplace">
                              <span>Pet Store and Marketplace</span>
                            </MultiSelectorItem>
                            <MultiSelectorItem value="E-commerce Storefront">
                              <span>E-commerce Storefront</span>
                            </MultiSelectorItem>
                            <MultiSelectorItem value="Landing Pages">
                              <span>Landing Pages</span>
                            </MultiSelectorItem>
                            <MultiSelectorItem value="Portfolio">
                              <span>Portfolio</span>
                            </MultiSelectorItem>
                            <MultiSelectorItem value="Blog">
                              <span>Blog</span>
                            </MultiSelectorItem>
                            <MultiSelectorItem value="SaaS">
                              <span>SaaS</span>
                            </MultiSelectorItem>
                            <MultiSelectorItem value="AI Tools">
                              <span>AI Tools</span>
                            </MultiSelectorItem>
                            <MultiSelectorItem value="Internal Tools">
                              <span>Internal Tools</span>
                            </MultiSelectorItem>
                            <MultiSelectorItem value="Marketing">
                              <span>Marketing</span>
                            </MultiSelectorItem>
                            <MultiSelectorItem value="Community">
                              <span>Community</span>
                            </MultiSelectorItem>
                            <MultiSelectorItem value="Prototype">
                              <span>Prototype</span>
                            </MultiSelectorItem>
                          </MultiSelectorList>
                        </MultiSelectorContent>
                      </MultiSelector>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="device_view"
                  render={({ field }) => (
                    <FormItem className="space-y-3">
                      <FormLabel>Default Device View</FormLabel>
                      <FormControl>
                        <RadioGroup
                          onValueChange={field.onChange}
                          defaultValue={field.value}
                          className="flex space-x-4"
                        >
                          <FormItem className="flex items-center space-x-2 space-y-0">
                            <FormControl>
                              <RadioGroupItem
                                value="desktop"
                                id="edit-desktop"
                              />
                            </FormControl>
                            <FormLabel
                              htmlFor="edit-desktop"
                              className="flex items-center gap-1 cursor-pointer"
                            >
                              <Monitor className="h-4 w-4" /> Desktop
                            </FormLabel>
                          </FormItem>
                          <FormItem className="flex items-center space-x-2 space-y-0">
                            <FormControl>
                              <RadioGroupItem value="mobile" id="edit-mobile" />
                            </FormControl>
                            <FormLabel
                              htmlFor="edit-mobile"
                              className="flex items-center gap-1 cursor-pointer"
                            >
                              <Smartphone className="h-4 w-4" /> Mobile
                            </FormLabel>
                          </FormItem>
                        </RadioGroup>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="ai_prompt"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>AI Prompt</FormLabel>
                      <FormControl>
                        <Textarea
                          placeholder="Enter AI prompt"
                          className="min-h-[100px]"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="ai_template_name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>AI Template Name</FormLabel>
                      <FormControl>
                        <Input
                          placeholder="Enter AI generated title (max 5 words)"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="ai_description"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>AI Description</FormLabel>
                      <FormControl>
                        <Textarea
                          placeholder="Enter AI description"
                          className="min-h-[100px]"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="share_twitter_tag"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Twitter Tag</FormLabel>
                      <FormControl>
                        <Textarea
                          placeholder=""
                          className="min-h-[60px]"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="share_social_tag"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Social Tag</FormLabel>
                      <FormControl>
                        <Textarea
                          placeholder=""
                          className="min-h-[60px]"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="share_linkedin_tag"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>LinkedIn Tag</FormLabel>
                      <FormControl>
                        <Textarea
                          placeholder=""
                          className="min-h-[60px]"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                {/* AI Overview Section */}
                <div className="space-y-4 border p-4 rounded-lg">
                  <h3 className="text-sm font-medium">AI Overview</h3>

                  <FormField
                    control={form.control}
                    name="ai_overview.title"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Overview Title</FormLabel>
                        <FormControl>
                          <Input
                            placeholder="Enter overview title"
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="ai_overview.content"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Overview Content</FormLabel>
                        <FormControl>
                          <Textarea
                            placeholder="Enter overview content"
                            className="min-h-[100px]"
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  {/* Features Array */}
                  <div className="space-y-2">
                    <FormLabel>Features</FormLabel>
                    {features.map((feature, index) => (
                      <div key={index} className="flex gap-2">
                        <Input
                          value={feature}
                          onChange={(e) => updateFeature(index, e.target.value)}
                          placeholder={`Feature ${index + 1}`}
                          className="flex-1"
                        />
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => removeFeature(index)}
                          disabled={features.length === 1}
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                    ))}
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={addFeature}
                      className="flex items-center gap-1"
                    >
                      <Plus className="h-4 w-4" />
                      Add Feature
                    </Button>
                  </div>

                  {/* Use Cases Array */}
                  <div className="space-y-2">
                    <FormLabel>Use Cases</FormLabel>
                    {usecases.map((usecase, index) => (
                      <div key={index} className="flex gap-2">
                        <Input
                          value={usecase}
                          onChange={(e) => updateUsecase(index, e.target.value)}
                          placeholder={`Use case ${index + 1}`}
                          className="flex-1"
                        />
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => removeUsecase(index)}
                          disabled={usecases.length === 1}
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                    ))}
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={addUsecase}
                      className="flex items-center gap-1"
                    >
                      <Plus className="h-4 w-4" />
                      Add Use Case
                    </Button>
                  </div>
                </div>
                {/* AI Tips Section */}
                <div className="space-y-4 border p-4 rounded-lg">
                  <h3 className="text-sm font-medium">AI Tips</h3>

                  <FormField
                    control={form.control}
                    name="ai_tips.summary"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Tips Summary</FormLabel>
                        <FormControl>
                          <Textarea
                            placeholder="Enter AI tips summary"
                            className="min-h-[100px]"
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  {/* Tips Points Array */}
                  <div className="space-y-2">
                    <FormLabel>Tips Points</FormLabel>
                    {tipsPoints.map((point, index) => (
                      <div key={index} className="flex gap-2">
                        <Input
                          value={point}
                          onChange={(e) =>
                            updateTipsPoint(index, e.target.value)
                          }
                          placeholder={`Point ${index + 1}`}
                          className="flex-1"
                        />
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => removeTipsPoint(index)}
                          disabled={tipsPoints.length === 1}
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                    ))}
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={addTipsPoint}
                      className="flex items-center gap-1"
                    >
                      <Plus className="h-4 w-4" />
                      Add Point
                    </Button>
                  </div>

                  {/* Target Users Array */}
                  <div className="space-y-2">
                    <FormLabel>Target Users</FormLabel>
                    {tipsTargetUsers.map((user, index) => (
                      <div key={index} className="flex gap-2">
                        <Input
                          value={user}
                          onChange={(e) =>
                            updateTipsTargetUser(index, e.target.value)
                          }
                          placeholder={`Target user ${index + 1}`}
                          className="flex-1"
                        />
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => removeTipsTargetUser(index)}
                          disabled={tipsTargetUsers.length === 1}
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                    ))}
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={addTipsTargetUser}
                      className="flex items-center gap-1"
                    >
                      <Plus className="h-4 w-4" />
                      Add Target User
                    </Button>
                  </div>
                </div>
                {/* AI FAQ Section */}
                <div className="space-y-4 border p-4 rounded-lg">
                  <h3 className="text-sm font-medium">AI FAQ</h3>

                  {/* FAQ Items Array */}
                  <div className="space-y-4">
                    <FormLabel>FAQ Items</FormLabel>
                    {faqItems.map((item, index) => (
                      <div
                        key={index}
                        className="space-y-2 p-3 border rounded-md"
                      >
                        <div className="flex justify-between items-center">
                          <span className="text-sm font-medium">
                            FAQ {index + 1}
                          </span>
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => removeFaqItem(index)}
                            disabled={faqItems.length === 1}
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        </div>
                        <div className="space-y-2">
                          <Input
                            value={item.question}
                            onChange={(e) =>
                              updateFaqItem(index, 'question', e.target.value)
                            }
                            placeholder="Enter question"
                          />
                          <Textarea
                            value={item.answer}
                            onChange={(e) =>
                              updateFaqItem(index, 'answer', e.target.value)
                            }
                            placeholder="Enter answer"
                            className="min-h-[80px]"
                          />
                        </div>
                      </div>
                    ))}
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={addFaqItem}
                      className="flex items-center gap-1"
                    >
                      <Plus className="h-4 w-4" />
                      Add FAQ Item
                    </Button>
                  </div>
                </div>
                <FormField
                  control={form.control}
                  name="image"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Screenshot</FormLabel>

                      {/* Show the current image if we have one */}
                      {hasImage && (
                        <div className="mb-4">
                          {imageFile ? (
                            // Show uploaded file name
                            <div className="flex items-center justify-between p-2 border rounded-md">
                              <p className="text-sm text-gray-600">
                                {imageFile.name}
                              </p>
                              <Button
                                type="button"
                                variant="destructive"
                                size="sm"
                                onClick={clearImage}
                              >
                                Remove
                              </Button>
                            </div>
                          ) : (
                            existingImageUrl && (
                              // Show existing thumbnail
                              <div>
                                <img
                                  src={existingImageUrl}
                                  alt="Template thumbnail"
                                  className="rounded-lg max-h-[100px] object-cover mb-2"
                                />
                                <Button
                                  type="button"
                                  variant="destructive"
                                  size="sm"
                                  onClick={clearImage}
                                >
                                  Remove
                                </Button>
                              </div>
                            )
                          )}
                        </div>
                      )}

                      {/* Show upload button if no image */}
                      {!hasImage && (
                        <FormControl>
                          <Input
                            type="file"
                            accept="image/jpeg,image/jpg,image/png"
                            onChange={(e) => field.onChange(e.target.files)}
                          />
                        </FormControl>
                      )}

                      <FormMessage />
                    </FormItem>
                  )}
                />

                {/* Template Remix Fields */}
                <div className="space-y-4 border-t pt-4">
                  <h3 className="text-lg font-semibold">
                    Template Remix Settings
                  </h3>
                  <FormField
                    control={form.control}
                    name="template_remix_title"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Remix Page Title</FormLabel>
                        <FormControl>
                          <Input {...field} placeholder="Start Your Project" />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="template_remix_subtitle"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Remix Page Subtitle</FormLabel>
                        <FormControl>
                          <Input
                            {...field}
                            placeholder="Tell us about your project"
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="template_remix_prompt_placeholder"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Prompt Placeholder Text</FormLabel>
                        <FormControl>
                          <Input {...field} placeholder="Tell us..." />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <FormField
                  control={form.control}
                  name="ranking"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Ranking (排序权重)</FormLabel>
                      <FormControl>
                        <Input
                          {...field}
                          type="number"
                          min={0}
                          placeholder="数字越大排序越靠前，默认为 0"
                          onChange={(e) =>
                            field.onChange(Number(e.target.value))
                          }
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </TabsContent>

              <TabsContent value="questions" className="space-y-6 mt-6">
                <div className="space-y-6">
                  <div>
                    <h3 className="text-lg font-medium">Questions Rich Text</h3>
                    <p className="text-sm text-muted-foreground mt-2">
                      Questions and content that will be displayed to help users
                      understand the template better. Use **bold text** for
                      question headers and format as needed.
                    </p>
                  </div>

                  <FormField
                    control={form.control}
                    name="questions_rich_text"
                    render={({ field }) => (
                      <FormItem className="space-y-2">
                        <FormLabel>Questions Content</FormLabel>
                        <FormControl>
                          <RichTextEditor
                            value={field.value || ''}
                            onChange={field.onChange}
                          />
                        </FormControl>
                        <p className="text-sm text-muted-foreground">
                          Use the rich text toolbar to format your questions
                          with bold, italic, lists, and links. This content will
                          be displayed with full formatting support.
                        </p>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              </TabsContent>

              <TabsContent value="advanced" className="space-y-6 mt-6">
                <p className="text-sm text-muted-foreground">
                  Advanced template settings and configurations.
                </p>
              </TabsContent>
            </Tabs>
          </form>
        </Form>
      </div>
      <DialogFooter className="pt-4 border-t">
        <Button type="submit" form="edit-template-form" disabled={loading}>
          {loading ? 'Updating...' : 'Update Template'}
        </Button>
      </DialogFooter>
    </DialogContent>
  )
}
